/**
 * BackgroundProvider component
 *
 * A context provider that manages the neural network background state and detection
 * across the application, simplifying the complex detection logic.
 */
import React, { useState, useCallback, createContext, ReactNode, useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";

import { logger } from "../utils/logger";

// Create a namespaced logger for background
const backgroundLogger = logger.getNamespacedLogger('Background');

// Define available background variants
export type BackgroundVariant = 'default' | 'services';

// Define the context interface
export interface BackgroundContextType {
  /** Whether the background is ready and initialized */
  isReady: boolean;
  
  /** The current background variant based on the current route */
  variant: BackgroundVariant;
  
  /** Callback to manually signal that the background is ready */
  setBackgroundReady: () => void;
  
  /** Whether the content should be shown (either because background is ready or fallback timer expired) */
  showContent: boolean;

  /** Function to manually set the background variant */
  setVariant: (variant: BackgroundVariant) => void;
}

// Create the context with undefined as initial value
export const BackgroundContext = createContext<BackgroundContextType | undefined>(undefined);

interface BackgroundProviderProps {
  /** Initial variant for the background (default: 'default') */
  initialVariant?: BackgroundVariant;
  
  /** Fallback timeout in ms before showing content even if background is not ready (default: 700) */
  fallbackTimeout?: number;
  
  /** Whether to add CSS fixes to ensure background visibility (default: true) */
  applyStyleFixes?: boolean;
  
  /** Enable debug logging (default: false) */
  debug?: boolean;
  
  /** Children components */
  children: ReactNode;
}

/**
 * BackgroundProvider component
 *
 * @param props - Component props
 * @returns BackgroundProvider component
 */
const BackgroundProvider: React.FC<BackgroundProviderProps> = ({
  initialVariant = 'default',
  fallbackTimeout = 700,
  applyStyleFixes = true,
  debug = false,
  children
}) => {
  const location = useLocation();
  const [isReady, setIsReady] = useState<boolean>(false);
  const [showContent, setShowContent] = useState<boolean>(false);
  const [manualVariant, setManualVariant] = useState<BackgroundVariant | null>(null);
  const [isTransitioning, setIsTransitioning] = useState<boolean>(false);
  const [prevVariant, setPrevVariant] = useState<BackgroundVariant>(initialVariant);
  
  // Determine background variant based on the current path or manual override
  const variant = useMemo<BackgroundVariant>(() => {
    // If manually set, use that variant
    if (manualVariant !== null) {
      return manualVariant;
    }
    
    // Otherwise determine based on path
    const path = location.pathname;
    if (path.includes('/services')) return 'services';
    return 'default';
  }, [location.pathname, manualVariant]);
  
  // Handle background variant changes with smoother transitions
  useEffect(() => {
    // Skip if it's the initial render
    if (prevVariant === variant) return;
    
    // Mark that we're transitioning
    setIsTransitioning(true);
    
    // Apply a short delay before considering the background "not ready"
    // This prevents content flickering during transitions
    const transitionDelay = setTimeout(() => {
      setIsTransitioning(false);
      setPrevVariant(variant);
    }, 500);
    
    return () => clearTimeout(transitionDelay);
  }, [variant, prevVariant]);
  
  // Callback to manually signal that the background is ready
  const setBackgroundReady = useCallback(() => {
    if (debug) backgroundLogger.debug('Background ready callback called');
    setIsReady(true);
    setShowContent(true);
  }, [debug]);
  
  // Function to manually set the background variant
  const setVariant = useCallback((newVariant: BackgroundVariant) => {
    if (debug) backgroundLogger.debug(`Setting background variant to: ${newVariant}`);
    setManualVariant(newVariant);
  }, [debug]);
  
  // Apply style fixes to ensure proper background rendering
  useEffect(() => {
    if (!applyStyleFixes) return;
    
    if (debug) backgroundLogger.debug("Applying background style fixes (Full Restore)");
    
    // Add style tag for background fixes
    const styleElement = document.createElement('style');
    styleElement.textContent = `
      /* Ensure neural network background is visible */
      #neural-network-background {
        position: fixed !important;
        z-index: -10 !important;
        visibility: visible !important;
        opacity: 1 !important;
      }
      
      #neural-network-background canvas {
        position: absolute !important;
        visibility: visible !important;
        opacity: 1 !important;
      }
      
      /* Ensure body doesn't have a background that covers our canvas */
      body, #root, .App {
        background-color: transparent !important;
        min-height: 100vh; /* Ensure the page is always at least full height */
      }
    `;
    document.head.appendChild(styleElement);
    
    // Set body background color that won't interfere with the canvas
    document.body.style.backgroundColor = 'transparent';
    
    // Clean up style element on unmount
    return () => {
      document.head.removeChild(styleElement);
    };
  }, [applyStyleFixes, debug]);
  
  // Detect background element and set ready state
  useEffect(() => {
    // Skip if already ready or transitioning
    if (isReady && !isTransitioning) return;
    
    if (debug) backgroundLogger.debug("Starting background detection");
    
    // Check for background element
    const checkBackgroundElement = () => {
      const bgElement = document.getElementById('neural-network-background');
      const canvasElement = document.querySelector('.neural-network-canvas');
      
      if (bgElement || canvasElement) {
        if (debug) backgroundLogger.debug("Background element found");
        setIsReady(true);
        setShowContent(true);
        return true;
      }
      return false;
    };
    
    // Try to detect background immediately
    if (checkBackgroundElement()) {
      if (debug) backgroundLogger.debug("Background detected immediately");
    } else {
      // Set a sequence of checks with increasing delays
      const checkIntervals = [10, 50, 100, 300, 500];
      const checkTimers = checkIntervals.map(interval => 
        setTimeout(() => {
          if (!isReady && checkBackgroundElement()) {
            if (debug) backgroundLogger.debug(`Background detected after ${interval}ms`);
          }
        }, interval)
      );
      
      // Fallback timeout - show content even if background isn't ready
      const fallbackTimer = setTimeout(() => {
        if (!isReady) {
          if (debug) backgroundLogger.debug("Fallback timeout reached, showing content anyway");
          setShowContent(true);
        }
      }, fallbackTimeout);
      
      // Clean up timers on unmount
      return () => {
        checkTimers.forEach(timer => clearTimeout(timer));
        clearTimeout(fallbackTimer);
      };
    }
  }, [isReady, fallbackTimeout, isTransitioning, debug]);
  
  // Create context value
  const contextValue = useMemo<BackgroundContextType>(() => {
    return {
      isReady: isReady && !isTransitioning,
      variant,
      setBackgroundReady,
      showContent,
      setVariant
    };
  }, [isReady, variant, setBackgroundReady, setVariant, isTransitioning, showContent]);
  
  return (
    <BackgroundContext.Provider value={contextValue}>
      {children}
    </BackgroundContext.Provider>
  );
};

export default BackgroundProvider; 
